import { Building2Icon, LogOutIcon, UserIcon, ChevronUpIcon, ChevronRightIcon } from 'lucide-react'
import { useState, useRef, useEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import { useAuthStore } from '../store/auth'
import type { User } from '../types'
import { OrgOptionList } from './OrgSwitcher'

type UserMenuProps = {
  user: User | null
  logout: () => void
  profilePath: string
  badge?: string
  badgeColor?: string
}

export function UserMenu({
  user,
  logout,
  profilePath,
  badge,
  badgeColor = 'bg-green-600',
}: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [orgOpen, setOrgOpen] = useState(false)
  const [orgPos, setOrgPos] = useState<{ left?: number; right?: number; bottom: number }>({
    bottom: 0,
  })
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const orgButtonRef = useRef<HTMLButtonElement>(null)
  const orgFlyoutRef = useRef<HTMLDivElement>(null)
  const firstActionRef = useRef<HTMLAnchorElement>(null)
  const wasOpenRef = useRef(false)
  const menuId = useId()
  const orgMenuId = useId()
  const { memberships } = useAuthStore()
  const hasMultipleOrgs = !!(memberships && memberships.length > 1)

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      const insideMenu = menuRef.current?.contains(target)
      const insideFlyout = orgFlyoutRef.current?.contains(target)
      if (!insideMenu && !insideFlyout) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  // Fecha o flyout de orgnizacoes quando o menu principal fecha
  useEffect(() => {
    if (!isOpen) {
      setOrgOpen(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      firstActionRef.current?.focus()
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus()
    }

    wasOpenRef.current = isOpen
  }, [isOpen])

  const toggleOrgMenu = () => {
    if (orgOpen) {
      setOrgOpen(false)
      return
    }

    const rect = orgButtonRef.current?.getBoundingClientRect()
    if (!rect) return

    const width = Math.min(280, window.innerWidth - 16)
    const left = rect.right + 8
    const fitsRight = left + width <= window.innerWidth - 8
    setOrgPos(
      fitsRight
        ? { left, bottom: window.innerHeight - rect.bottom }
        : { right: 8, bottom: window.innerHeight - rect.bottom },
    )
    setOrgOpen(true)
  }

  const displayName = user?.full_name || user?.email || ''
  const initials = user?.full_name
    ? user.full_name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?'

  return (
    <div ref={menuRef} className="relative flex-shrink-0 border-t border-gray-800">
      {/* Popover menu */}
      {isOpen && (
        <div
          id={menuId}
          role="menu"
          aria-label="Menu da conta"
          className="absolute right-0 bottom-full left-0 z-50 mx-2 mb-1 rounded-lg border border-gray-700 bg-gray-800 shadow-lg"
        >
          {/* User info header */}
          <div className="rounded-t-lg border-b border-gray-700 px-4 py-3">
            <p className="truncate text-sm font-medium text-white">
              {user?.full_name || 'Usuário'}
            </p>
            <p className="truncate text-xs text-gray-400">{user?.email}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <Link
              ref={firstActionRef}
              to={profilePath}
              onClick={() => setIsOpen(false)}
              role="menuitem"
              className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-300 transition-colors hover:bg-gray-700 hover:text-white"
            >
              <UserIcon className="h-4 w-4" />
              <span>Meu Perfil</span>
            </Link>

            {hasMultipleOrgs && (
              <div className="relative">
                <button
                  ref={orgButtonRef}
                  type="button"
                  onClick={toggleOrgMenu}
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={orgOpen}
                  aria-controls={orgMenuId}
                  className="flex w-full items-center space-x-3 px-4 py-2.5 text-sm text-gray-300 transition-colors hover:bg-gray-700 hover:text-white"
                >
                  <Building2Icon className="h-4 w-4" />
                  <span className="flex-1 text-left">Organizações</span>
                  <ChevronRightIcon
                    className={`h-4 w-4 text-gray-500 transition-transform ${orgOpen ? 'rotate-90' : ''}`}
                  />
                </button>

                {orgOpen &&
                  createPortal(
                    <div
                      id={orgMenuId}
                      ref={orgFlyoutRef}
                      role="menu"
                      aria-label="Organizações"
                      style={orgPos}
                      className="fixed z-[60] w-max max-w-[85vw] min-w-[280px] rounded-lg border border-gray-700 bg-gray-800 py-1 shadow-xl"
                    >
                      <div className="px-4 py-2 text-xs font-medium tracking-wide text-gray-400 uppercase">
                        Organizações
                      </div>
                      <div className="max-h-[286px] [scrollbar-width:thin] [scrollbar-color:#4b5563_#1f2937] overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:h-[6px] [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:hover:bg-gray-500 [&::-webkit-scrollbar-track]:bg-transparent [&>button]:h-[52px]">
                        <OrgOptionList
                          onSelect={() => {
                            setOrgOpen(false)
                            setIsOpen(false)
                          }}
                        />
                      </div>
                      <div className="px-4 py-2 text-[11px] text-gray-500">
                        A troca abre a organização em nova aba
                      </div>
                    </div>,
                    document.body,
                  )}
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setIsOpen(false)
                logout()
              }}
              role="menuitem"
              className="flex w-full items-center space-x-3 px-4 py-2.5 text-sm text-gray-300 transition-colors hover:bg-gray-700 hover:text-red-400"
            >
              <LogOutIcon className="h-4 w-4" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setIsOpen(true)
          }
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        className="flex w-full cursor-pointer items-center space-x-3 p-4 transition-colors hover:bg-gray-800/50"
      >
        {/* Avatar */}
        {user?.profile_photo_url ? (
          <img
            src={user.profile_photo_url}
            alt={user.full_name}
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
            <span className="text-xs font-medium text-gray-300">{initials}</span>
          </div>
        )}

        {/* Name + badge */}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center space-x-2">
            <p className="truncate text-sm font-medium text-gray-200">{displayName}</p>
            {badge && (
              <span
                className={`flex-shrink-0 px-1.5 py-0.5 ${badgeColor} rounded text-[10px] font-medium text-white`}
              >
                {badge}
              </span>
            )}
          </div>
        </div>

        {/* Chevron */}
        <ChevronUpIcon
          className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? '' : 'rotate-180'}`}
        />
      </button>
    </div>
  )
}
